import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Video } from '@blackblack/shared';
import { usePlayer } from '../../state/player';
import { Skeleton, Spinner } from '../../components/ui/primitives';
import { VideoCard } from './VideoCard';
import styles from './feed.module.css';

interface Props {
  items: Array<Video & { _key?: string }>;
  hasMore: boolean;
  loading: boolean;
  onNearEnd: () => void;
  active: boolean;
  initialIndex?: number;
  empty?: ReactNode;
}

/** How many cards around the active one get a mounted <video> (preload window). */
const WINDOW_BEHIND = 1;
const WINDOW_AHEAD = 2;

/**
 * Vertical, snap-scrolling, endless feed. Only cards near the viewport mount
 * a <video> element (memory & bandwidth on mobile); the next one preloads.
 */
export function VerticalFeed({ items, hasMore, loading, onNearEnd, active, initialIndex = 0, empty }: Props) {
  const scroller = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(initialIndex);
  const [visible, setVisible] = useState(!document.hidden);
  const nowPlayingOpen = usePlayer((s) => s.expanded);
  const sessionId = useMemo(() => Math.random().toString(36).slice(2), []);

  // Jump to the initial item once.
  useEffect(() => {
    if (initialIndex > 0) scroller.current?.scrollTo({ top: initialIndex * (scroller.current?.clientHeight ?? 0) });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track which card is on screen.
  useEffect(() => {
    const root = scroller.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) setCurrent(Number((e.target as HTMLElement).dataset.index));
      },
      { root, threshold: 0.6 },
    );
    root.querySelectorAll<HTMLElement>('[data-index]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [items.length]);

  useEffect(() => {
    if (hasMore && !loading && current >= items.length - 3) onNearEnd();
  }, [current, items.length, hasMore, loading, onNearEnd]);

  useEffect(() => {
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Keyboard navigation (desktop): ↑/↓/j/k.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('input, textarea, [role=dialog]')) return;
      const h = scroller.current?.clientHeight ?? 0;
      if (e.key === 'ArrowDown' || e.key === 'j') scroller.current?.scrollBy({ top: h, behavior: 'smooth' });
      if (e.key === 'ArrowUp' || e.key === 'k') scroller.current?.scrollBy({ top: -h, behavior: 'smooth' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  // Re-selecting the Home tab scrolls back to the top.
  useEffect(() => {
    const onReselect = (e: Event) => (e as CustomEvent).detail === 'home' && scroller.current?.scrollTo({ top: 0, behavior: 'smooth' });
    window.addEventListener('bb:tab-reselect', onReselect);
    return () => window.removeEventListener('bb:tab-reselect', onReselect);
  }, []);

  if (!items.length && loading) return <FeedSkeleton />;
  if (!items.length && !loading) return <div className={styles.emptyWrap}>{empty}</div>;

  const playbackAllowed = active && visible && !nowPlayingOpen;

  return (
    <div ref={scroller} className={styles.scroller}>
      {items.map((v, i) => {
        const near = i >= current - WINDOW_BEHIND && i <= current + WINDOW_AHEAD;
        return (
          <div key={v._key ?? v.id} data-index={i} className={styles.slot}>
            {near || Math.abs(i - current) < 6 ? (
              <VideoCard video={v} active={i === current} near={near} playbackAllowed={playbackAllowed} sessionId={sessionId} />
            ) : null}
          </div>
        );
      })}
      {hasMore && (
        <div className={styles.slot}>
          <div className={styles.loadingSlot}>
            <Spinner />
          </div>
        </div>
      )}
    </div>
  );
}

function FeedSkeleton() {
  return (
    <div className={styles.skeleton} aria-busy="true" aria-label="Feed laden">
      <div className={styles.skeletonMeta}>
        <Skeleton w={140} h={16} />
        <Skeleton w="80%" h={14} />
        <Skeleton w="60%" h={14} />
      </div>
      <div className={styles.skeletonRail}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} w={40} h={40} r={20} />
        ))}
      </div>
    </div>
  );
}
